import React from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const placeholderFor = (value, text) => (toArray(value).length ? "" : text);

// ✅ helper to safely get a display label whether option is a string or {label, value}
const getLabel = (option) => {
  if (option === null || option === undefined) return "";
  return typeof option === "string" ? option : (option.label ?? "");
};

const renderScrollableTags = (value, getTagProps) => (
  <Box className="enhance-filter-tag-scroll">
    {value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip key={key} label={getLabel(option)} size="small" {...tagProps} />
      );
    })}
  </Box>
);

const FieldLabel = ({ children }) =>
  children ? (
    <Box
      component="span"
      className="enhance-filter-field-label"
      sx={{ display: "block" }}
    >
      {children}
    </Box>
  ) : null;

const ListboxWithLoader = React.forwardRef(
  function ListboxWithLoader(props, ref) {
    const { children, onLoadMore, hasMore, loadingMore, ...other } = props;

    const handleScroll = (event) => {
      const node = event.currentTarget;
      const reachedBottom =
        node.scrollTop + node.clientHeight >= node.scrollHeight - 20;

      if (reachedBottom && hasMore && !loadingMore && onLoadMore) {
        onLoadMore();
      }
    };

    return (
      <ul {...other} ref={ref} onScroll={handleScroll}>
        {children}

        {loadingMore && (
          <li className="enhance-filter-loadmore">
            <CircularProgress size={16} thickness={5} />
          </li>
        )}

        {!loadingMore && !hasMore && children?.length > 0 && (
          <li
            style={{
              textAlign: "center",
              padding: "10px",
              color: "#6B7280",
              fontSize: 13,
            }}
          >
            No more data
          </li>
        )}
      </ul>
    );
  },
);

const MultiSelectDropdown = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  wrapperClassName = "enhance-filter-field",
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  multiple = true,
  onInputChange,
}) => {
  const selected = multiple
    ? toArray(value)
    : value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ? null
      : value;

  return (
    <Box className={wrapperClassName}>
      <FieldLabel>{label}</FieldLabel>
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={multiple}
        loading={loading}
        className="enhance-filter-autocomplete"
        options={options}
        getOptionLabel={getLabel}
        isOptionEqualToValue={(option, val) => {
          const optVal = typeof option === "string" ? option : option?.value;
          const valVal = typeof val === "string" ? val : val?.value;
          return optVal === valVal;
        }}
        value={selected}
        onInputChange={onInputChange}
        onChange={(e, newValue) => onChange(newValue)}
        renderTags={multiple ? renderScrollableTags : undefined}
        PopperProps={{ className: "enhance-filter-popper" }}
        ListboxComponent={ListboxWithLoader}
        ListboxProps={{
          className: "enhance-filter-listbox",
          onLoadMore,
          hasMore,
          loadingMore,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholderFor(selected, placeholder)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={14}
                      thickness={5}
                      sx={{ mr: 1 }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default MultiSelectDropdown;
